/**
 * Application Configuration
 * Centralized configuration management using environment variables
 */

// Development fallbacks for missing environment variables
const developmentDefaults = {
  API_BASE_URL: '/api/v1',
  API_TIMEOUT: 30000,
  BACKEND_URL: 'http://localhost:8034',
  DEV_PORT: 8022,
  AUTH_TOKEN_KEY: 'auth_token',
  LOGIN_REDIRECT: '/login',
  DASHBOARD_REDIRECT: '/dashboard',
  HEALTH_REFRESH_INTERVAL: 30000,
  CRITICAL_SERVICES: 'mcp_orchestrator,auth_service,notification_service,medication_data_service',
  SNACKBAR_TIMEOUT: 3000,
  PRIMARY_COLOR: '#1976D2',
  SECONDARY_COLOR: '#424242',
  ACCENT_COLOR: '#82B1FF',
  ERROR_COLOR: '#FF5252',
  INFO_COLOR: '#2196F3',
  SUCCESS_COLOR: '#4CAF50',
  WARNING_COLOR: '#FFC107',
  APP_NAME: 'Dhara Admin Portal',
  APP_VERSION: '1.0.0',
  COMPANY_NAME: 'Aushadh Health Solutions'
}

// Helper function to get environment variable with fallback
const getEnvVar = (key, fallback = null) => {
  const envKey = `VITE_${key}`
  const value = import.meta.env[envKey]
  
  if (value === undefined || value === null || value === '') {
    if (import.meta.env.DEV && fallback !== null) {
      console.warn(`Environment variable ${envKey} not set, using development default: ${fallback}`)
      return fallback
    }
    if (fallback === null) {
      throw new Error(`Required environment variable ${envKey} is not set`)
    }
    return fallback
  }
  
  return value
}

// Parse comma-separated list
const parseList = (str) => str.split(',').map(item => item.trim()).filter(item => item.length > 0)

// Parse number with validation
const parseNumber = (str, defaultValue) => {
  const num = parseInt(str, 10)
  return isNaN(num) ? defaultValue : num
}

// Application Configuration Object
export const config = {
  // API Configuration
  api: {
    baseURL: getEnvVar('BACKEND_URL', developmentDefaults.BACKEND_URL) + '/api/v1',
    timeout: parseNumber(getEnvVar('API_TIMEOUT', developmentDefaults.API_TIMEOUT), 30000),
    backendURL: getEnvVar('BACKEND_URL', developmentDefaults.BACKEND_URL)
  },

  // Server Configuration
  server: {
    devPort: parseNumber(getEnvVar('DEV_PORT', developmentDefaults.DEV_PORT), 8022),
    productionPort: parseNumber(getEnvVar('PRODUCTION_PORT', developmentDefaults.DEV_PORT), 8022)
  },

  // Authentication
  auth: {
    tokenKey: getEnvVar('AUTH_TOKEN_KEY', developmentDefaults.AUTH_TOKEN_KEY),
    loginRedirect: getEnvVar('LOGIN_REDIRECT', developmentDefaults.LOGIN_REDIRECT),
    dashboardRedirect: getEnvVar('DASHBOARD_REDIRECT', developmentDefaults.DASHBOARD_REDIRECT)
  },

  // Health Monitoring
  health: {
    refreshInterval: parseNumber(getEnvVar('HEALTH_REFRESH_INTERVAL', developmentDefaults.HEALTH_REFRESH_INTERVAL), 30000),
    criticalServices: parseList(getEnvVar('CRITICAL_SERVICES', developmentDefaults.CRITICAL_SERVICES))
  },

  // UI Configuration
  ui: {
    snackbarTimeout: parseNumber(getEnvVar('SNACKBAR_TIMEOUT', developmentDefaults.SNACKBAR_TIMEOUT), 3000),
    theme: {
      primary: getEnvVar('PRIMARY_COLOR', developmentDefaults.PRIMARY_COLOR),
      secondary: getEnvVar('SECONDARY_COLOR', developmentDefaults.SECONDARY_COLOR),
      accent: getEnvVar('ACCENT_COLOR', developmentDefaults.ACCENT_COLOR),
      error: getEnvVar('ERROR_COLOR', developmentDefaults.ERROR_COLOR),
      info: getEnvVar('INFO_COLOR', developmentDefaults.INFO_COLOR),
      success: getEnvVar('SUCCESS_COLOR', developmentDefaults.SUCCESS_COLOR),
      warning: getEnvVar('WARNING_COLOR', developmentDefaults.WARNING_COLOR)
    }
  },

  // Application Info
  app: {
    name: getEnvVar('APP_NAME', developmentDefaults.APP_NAME),
    version: getEnvVar('APP_VERSION', developmentDefaults.APP_VERSION),
    companyName: getEnvVar('COMPANY_NAME', developmentDefaults.COMPANY_NAME)
  },

  // Development Settings
  dev: {
    enableDevTools: getEnvVar('ENABLE_DEV_TOOLS', 'false') === 'true',
    logLevel: getEnvVar('LOG_LEVEL', 'info')
  }
}

// Validation function
export const validateConfig = () => {
  const errors = []

  // Validate required configurations
  if (!config.api.baseURL) errors.push('API base URL is required')
  if (!config.auth.tokenKey) errors.push('Auth token key is required')
  if (config.health.criticalServices.length === 0) errors.push('At least one critical service must be defined')

  // Validate numeric values
  if (config.api.timeout < 1000) errors.push('API timeout must be at least 1000ms')
  if (config.health.refreshInterval < 5000) errors.push('Health refresh interval must be at least 5000ms')

  if (errors.length > 0) {
    console.error('Configuration validation failed:', errors)
    throw new Error(`Configuration validation failed: ${errors.join(', ')}`)
  }

  console.info('Configuration validation passed')
  return true
}

// Log configuration in development
if (import.meta.env.DEV) {
  console.group('Application Configuration')
  console.log('API Base URL:', config.api.baseURL)
  console.log('Backend URL:', config.api.backendURL)
  console.log('Auth Token Key:', config.auth.tokenKey)
  console.log('Critical Services:', config.health.criticalServices)
  console.log('Theme Colors:', config.ui.theme)
  console.groupEnd()
}

export default config